import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import type { MeetingAudience } from "@costadorada/shared";
import { api } from "@/lib/api";

export interface Recipient {
  group: MeetingAudience;
  name: string;
  email: string;
  unitNumber?: string;
}

export type RecipientDirectory = Record<MeetingAudience, Recipient[]>;

/**
 * Who an invitation goes to. Three independent sources that the server merges
 * and dedupes: whole categories, individually picked people, and outside guests.
 */
export interface InviteSelection {
  /** Categories invited wholesale — resolved server-side at send time. */
  groups: MeetingAudience[];
  /** Individually ticked portal recipients (lowercased emails). */
  emails: string[];
  /** Hand-typed guests who are not in the portal (lowercased emails). */
  extras: string[];
}

export const EMPTY_SELECTION: InviteSelection = { groups: [], emails: [], extras: [] };

/** Request body for POST /meetings/:id/invite. */
export function invitePayload(sel: InviteSelection) {
  return { groups: sel.groups, emails: sel.emails, extraEmails: sel.extras };
}

/** Best-effort recipient count for the UI; the server dedupes authoritatively. */
export function selectionCount(sel: InviteSelection, data?: RecipientDirectory): number {
  const all = new Set<string>();
  if (data) {
    for (const g of sel.groups) for (const r of data[g] ?? []) all.add(r.email.toLowerCase());
  }
  sel.emails.forEach((e) => all.add(e));
  sel.extras.forEach((e) => all.add(e));
  return all.size;
}

export function useRecipientDirectory(enabled = true) {
  return useQuery({
    queryKey: ["meeting-recipients"],
    queryFn: async () => (await api.get<RecipientDirectory>("/meetings/recipients")).data,
    enabled,
  });
}

/** Pre-select the audience a meeting type normally convenes. */
export function defaultGroupsFor(meetingType: string): MeetingAudience[] {
  return meetingType === "JUNTA" ? ["board"] : ["owners"];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AUDIENCE_ORDER: MeetingAudience[] = ["board", "admins", "owners", "residents"];

interface Props {
  value: InviteSelection;
  onChange: (next: InviteSelection) => void;
  data?: RecipientDirectory;
  isLoading?: boolean;
}

/**
 * Audience picker for meeting invitations: tick a whole category (Junta,
 * Administración, Titulares, Residentes), drill in to pick individuals, and add
 * outside guests by email.
 */
export function MeetingRecipientPicker({ value, onChange, data, isLoading }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<MeetingAudience | null>(null);
  const [guestDraft, setGuestDraft] = useState("");
  const [guestError, setGuestError] = useState<string | null>(null);

  const labels: Record<MeetingAudience, string> = {
    board: t("meetings.board"),
    admins: t("meetings.admins"),
    owners: t("meetings.owners"),
    residents: t("meetings.residents"),
  };

  const toggleGroup = (g: MeetingAudience) => {
    const on = value.groups.includes(g);
    const groupEmails = new Set((data?.[g] ?? []).map((r) => r.email.toLowerCase()));
    onChange({
      ...value,
      groups: on ? value.groups.filter((x) => x !== g) : [...value.groups, g],
      // Ticking the whole category subsumes any individual picks inside it.
      emails: on ? value.emails : value.emails.filter((e) => !groupEmails.has(e)),
    });
  };

  const toggleEmail = (email: string) => {
    const key = email.toLowerCase();
    onChange({
      ...value,
      emails: value.emails.includes(key)
        ? value.emails.filter((e) => e !== key)
        : [...value.emails, key],
    });
  };

  const addGuests = () => {
    // Accept one address or a pasted comma/semicolon/space-separated list.
    const candidates = guestDraft
      .split(/[,;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (!candidates.length) return;
    const bad = candidates.find((c) => !EMAIL_RE.test(c));
    if (bad) {
      setGuestError(t("meetings.invalidEmail", { email: bad }));
      return;
    }
    setGuestError(null);
    setGuestDraft("");
    onChange({ ...value, extras: Array.from(new Set([...value.extras, ...candidates])) });
  };

  const removeGuest = (email: string) =>
    onChange({ ...value, extras: value.extras.filter((e) => e !== email) });

  const total = selectionCount(value, data);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-sm text-slate-500">{t("common.loading")}</div>
        ) : (
          AUDIENCE_ORDER.map((g) => {
            const list = data?.[g] ?? [];
            if (!list.length) return null;
            const whole = value.groups.includes(g);
            const open = expanded === g;
            const picked = list.filter((r) => value.emails.includes(r.email.toLowerCase())).length;
            return (
              <div key={g} className="border border-slate-200 rounded">
                <div className="flex items-center gap-2 px-2 py-2 bg-slate-50">
                  <input
                    id={`aud-${g}`}
                    type="checkbox"
                    checked={whole}
                    onChange={() => toggleGroup(g)}
                  />
                  <label htmlFor={`aud-${g}`} className="text-sm font-medium flex-1 cursor-pointer">
                    {labels[g]}{" "}
                    <span className="text-slate-500 font-normal">
                      ({whole ? t("meetings.wholeCategory", { count: list.length }) : `${picked}/${list.length}`})
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => setExpanded(open ? null : g)}
                  >
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {t("meetings.choosePeople")}
                  </button>
                </div>
                {open && (
                  <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 border-t border-slate-200">
                    {list.map((r) => (
                      <label
                        key={r.email}
                        className={`flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-50 ${
                          whole ? "opacity-60" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={whole}
                          checked={whole || value.emails.includes(r.email.toLowerCase())}
                          onChange={() => toggleEmail(r.email)}
                        />
                        <span className="flex-1">
                          {r.name}
                          {r.unitNumber ? ` · ${r.unitNumber}` : ""}
                        </span>
                        <span className="text-xs text-slate-400">{r.email}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div>
        <label className="label">{t("meetings.extraGuests")}</label>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            type="text"
            placeholder={t("meetings.extraGuestsHint")}
            value={guestDraft}
            onChange={(e) => {
              setGuestDraft(e.target.value);
              setGuestError(null);
            }}
            onKeyDown={(e) => {
              // Enter adds a guest without submitting the surrounding form.
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addGuests();
              }
            }}
          />
          <button type="button" className="btn-secondary" onClick={addGuests} disabled={!guestDraft.trim()}>
            <Plus size={14} /> {t("meetings.addGuest")}
          </button>
        </div>
        {guestError && <p className="text-xs text-red-600 mt-1">{guestError}</p>}
        {value.extras.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {value.extras.map((e) => (
              <span
                key={e}
                className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded-full pl-2 pr-1 py-0.5"
              >
                {e}
                <button
                  type="button"
                  onClick={() => removeGuest(e)}
                  className="text-slate-500 hover:text-red-600"
                  aria-label={t("common.delete")}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="text-sm text-slate-500">
        {t("meetings.recipients")}: <span className="font-medium text-slate-700">{total}</span>
      </div>
    </div>
  );
}
